import React, { useEffect, useState } from 'react';
import { Outlet } from 'react-router-dom';
import { accessService, AccessState } from '../services/access.service';
import { authService } from '../services/auth.service';
import { billingService } from '../services/billing.service';
import { colocService } from '../services/coloc.service';
import type { Coloc } from '../types/coloc';
import { getErrorMessage } from '../utils/error';
import { UpgradeScreen } from './CapabilityGate';
import './AccessGate.css';

/**
 * Garde d'accès / abonnement (chantier 3A, saas/09). Enveloppe les routes owner.
 * - full    → laisse passer (<Outlet/>).
 * - grace/expired → écran de blocage (back-office coupé). Données préservées.
 *
 * Échec FERMÉ sur erreur réseau : l'API refuse déjà les écritures quand elle ne
 * peut pas vérifier l'accès (échec fermé côté middleware), donc laisser passer
 * ici ne donnerait qu'un back-office où chaque action échoue en 403 — pire
 * qu'un message clair. Le message distingue bien les deux causes : abonnement
 * terminé, ou vérification impossible.
 */
const AccessGate: React.FC = () => {
  const [state, setState] = useState<'loading' | 'ok' | 'unverifiable' | AccessState>('loading');
  const [redirection, setRedirection] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);
  // Pendant la grâce, le site reste en ligne 7 jours : le bailleur qui arrête
  // peut le retirer lui-même (ticket #11).
  const [enLigne, setEnLigne] = useState<Coloc[]>([]);
  const [erreurRetrait, setErreurRetrait] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    accessService
      .getState()
      .then(async (s) => {
        // Bloqué : on relit d'abord l'abonnement chez Stripe, qui rattrape un
        // message perdu. Ce rattrapage ne se faisait que dans « Mon compte »,
        // derrière cet écran : le blocage devenait définitif (ticket #10).
        if (s.access !== 'full') {
          try {
            await billingService.getSubscription();
            s = await accessService.getState();
          } catch {
            // Relecture impossible : l'état lu d'abord fait foi.
          }
        }
        if (cancelled) return;
        setState(s.access === 'full' ? 'ok' : s);
        if (s.access === 'grace') {
          colocService.getColocs()
            .then((colocs) => { if (!cancelled) setEnLigne(colocs.filter((c) => c.isPublished)); })
            .catch(() => { /* le retrait reste possible depuis le back-office une fois réabonné */ });
        }
      })
      .catch(() => {
        if (!cancelled) setState('unverifiable');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (state === 'loading') {
    return <div className="access-screen"><p>Chargement…</p></div>;
  }
  if (state === 'ok') {
    return <Outlet />;
  }

  if (state === 'unverifiable') {
    return (
      <div className="access-screen">
        <div className="access-card">
          <h1>Vérification impossible</h1>
          <p>
            Nous n&apos;arrivons pas à vérifier votre accès pour le moment. Ce
            n&apos;est pas lié à votre abonnement, et vos données ne sont pas
            affectées.
          </p>
          <p className="access-sub">
            Réessayez dans quelques instants. Si le problème persiste, écrivez-nous
            à <a href="mailto:contact@coloc.app">contact@coloc.app</a>.
          </p>
          <button className="access-logout" onClick={() => window.location.reload()}>
            Réessayer
          </button>
        </div>
      </div>
    );
  }

  // Paiement échoué : l'abonnement existe toujours chez Stripe, souscrire à
  // nouveau en créerait un second. On mène au portail pour changer de carte
  // (les routes /billing/ restent ouvertes à un compte bloqué).
  const gerer = async () => {
    setRedirection(true);
    setErreur(null);
    try {
      window.location.href = await billingService.createPortalSession();
    } catch (e) {
      setErreur(getErrorMessage(e, 'Accès à la gestion de votre abonnement impossible pour le moment.'));
      setRedirection(false);
    }
  };

  // Bloqué (grace ou expired). Message « compte plus vivant » — distinct du
  // message « votre plan ne contient pas X » (CapabilityGate) : ici l'action
  // proposée est la réactivation de l'abonnement via Stripe.
  return (
    <div className="access-screen">
      <div className="access-card" style={{ maxWidth: 820 }}>
        <h1>Votre accès a expiré</h1>
        <p>
          Votre période d&apos;accès à Coloc est terminée. Vos données sont
          conservées en sécurité — rien n&apos;est supprimé.
        </p>
        {state.status === 'past_due' ? (
          <>
            <p>
              Votre dernier paiement a échoué. Mettez à jour votre carte bancaire
              pour régler votre abonnement.
            </p>
            <button className="access-cta" onClick={gerer} disabled={redirection}>
              Gérer mon abonnement
            </button>
            {erreur && <p className="access-error">{erreur}</p>}
          </>
        ) : (
          <UpgradeScreen title="Réactivez votre abonnement" />
        )}
        {enLigne.length > 0 && state.grace_until && (
          <div className="access-sub">
            <p>
              Votre site reste en ligne jusqu&apos;au{' '}
              {new Date(state.grace_until).toLocaleDateString('fr-FR')}. Vous pouvez le retirer dès
              maintenant :
            </p>
            {enLigne.map((c) => (
              <button
                key={c.id}
                className="access-logout"
                onClick={async () => {
                  setErreurRetrait(null);
                  try {
                    await colocService.unpublish(c.id);
                    setEnLigne((l) => l.filter((x) => x.id !== c.id));
                  } catch (e) {
                    setErreurRetrait(getErrorMessage(e, 'Retrait du site impossible pour le moment.'));
                  }
                }}
              >
                Retirer le site de « {c.name} »
              </button>
            ))}
            {erreurRetrait && <p className="access-error">{erreurRetrait}</p>}
          </div>
        )}
        <p className="access-sub">
          Une question ? Écrivez-nous à{' '}
          <a href="mailto:contact@coloc.app">contact@coloc.app</a>.
        </p>
        <button className="access-logout" onClick={() => { authService.logout(); window.location.href = '/signin'; }}>
          Se déconnecter
        </button>
      </div>
    </div>
  );
};

export default AccessGate;
